import {
  getGemAmount,
  getPackageId,
  isFootballPackage,
  isGemPackage,
  isRetroFootballPackage
} from '@/data/retroFootball';

export const FOOTBALL_FULFILLMENT_URL = (
  process.env.REACT_APP_FOOTBALL_FULFILLMENT_URL ||
  'https://football-iap.retrostore.dev'
).replace(/\/+$/, '');

export const CLAIM_HISTORY_TTL_MS = 24 * 60 * 60 * 1000;
const CHECKOUT_CONTINUATION_TTL_MS = 30 * 60 * 1000;
const MAX_CLAIM_HISTORY = 5;

export const CLAIM_STORAGE_KEYS = Object.freeze({
  ACTIVE: 'retrostore_football_claim_active',
  HISTORY: 'retrostore_football_claim_history',
  CHECKOUT: 'retrostore_checkout_continuation'
});

const getStorage = (type) => {
  if (typeof window === 'undefined') return null;
  return type === 'session' ? window.sessionStorage : window.localStorage;
};

const readJson = (storage, key, fallback) => {
  if (!storage) return fallback;
  try {
    const value = storage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    storage.removeItem(key);
    return fallback;
  }
};

const writeJson = (storage, key, value) => {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in strict privacy modes. Checkout can still
    // continue, but the delivery card will not survive a full navigation.
  }
};

const isFresh = (createdAt, ttl = CLAIM_HISTORY_TTL_MS) => {
  const timestamp = Number(createdAt);
  return Number.isFinite(timestamp) && Date.now() - timestamp <= ttl;
};

const bytesToBase64Url = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

export const generateClaimToken = () => {
  if (
    typeof window === 'undefined' ||
    !window.crypto ||
    typeof window.crypto.getRandomValues !== 'function'
  ) {
    throw new Error('Secure checkout token generation is not available.');
  }

  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
};

const sanitizeClaimItems = (items) => (
  (Array.isArray(items) ? items : [])
    .filter(isRetroFootballPackage)
    .map((item) => ({
      packageId: getPackageId(item),
      quantity: Number(item.quantity ?? item.qty ?? 1),
      gems: getGemAmount(item),
      giftTo: item.giftTo || item.targetUsername || null
    }))
);

const pruneHistory = (history) => {
  const freshEntries = Object.entries(history || {})
    .filter(([, record]) => isFresh(record?.createdAt))
    .sort(([, left], [, right]) => Number(right.createdAt) - Number(left.createdAt))
    .slice(0, MAX_CLAIM_HISTORY);
  return Object.fromEntries(freshEntries);
};

export const clearActiveFootballClaim = () => {
  getStorage('session')?.removeItem(CLAIM_STORAGE_KEYS.ACTIVE);
  getStorage('local')?.removeItem(CLAIM_STORAGE_KEYS.ACTIVE);
};

export const createFootballClaim = (items) => {
  const claimItems = sanitizeClaimItems(items);
  if (!claimItems.length) {
    clearActiveFootballClaim();
    return null;
  }

  const token = generateClaimToken();
  const createdAt = Date.now();
  const uses = [];
  if (claimItems.some(isGemPackage)) uses.push('gem');
  if (claimItems.some(isFootballPackage)) uses.push('license');

  const giftRecipient = claimItems.find(
    (item) => isFootballPackage(item) && item.giftTo
  )?.giftTo || null;

  const record = {
    createdAt,
    items: claimItems,
    uses,
    giftRecipient
  };

  const localStorage = getStorage('local');
  const history = pruneHistory(readJson(
    localStorage,
    CLAIM_STORAGE_KEYS.HISTORY,
    {}
  ));
  history[token] = record;

  writeJson(localStorage, CLAIM_STORAGE_KEYS.HISTORY, pruneHistory(history));
  getStorage('session')?.setItem(CLAIM_STORAGE_KEYS.ACTIVE, token);

  return Object.freeze({
    token,
    ...record,
    items: Object.freeze(record.items.map((item) => Object.freeze({ ...item }))),
    uses: Object.freeze([...record.uses])
  });
};

export const activateFootballClaim = (claimToken) => {
  if (!claimToken) return null;
  const history = pruneHistory(readJson(
    getStorage('local'),
    CLAIM_STORAGE_KEYS.HISTORY,
    {}
  ));
  const record = history[claimToken];
  if (!record || !isFresh(record.createdAt)) return null;

  getStorage('session')?.setItem(CLAIM_STORAGE_KEYS.ACTIVE, claimToken);
  return { token: claimToken, ...record };
};

export const getActiveFootballClaim = () => {
  const sessionStorage = getStorage('session');
  const localStorage = getStorage('local');
  const token = sessionStorage?.getItem(CLAIM_STORAGE_KEYS.ACTIVE);
  if (!token) return null;

  const history = pruneHistory(readJson(
    localStorage,
    CLAIM_STORAGE_KEYS.HISTORY,
    {}
  ));
  writeJson(localStorage, CLAIM_STORAGE_KEYS.HISTORY, history);

  const record = history[token];
  if (!record || !isFresh(record.createdAt)) {
    clearActiveFootballClaim();
    return null;
  }

  sessionStorage?.setItem(CLAIM_STORAGE_KEYS.ACTIVE, token);
  return { token, ...record };
};

export const storeCheckoutContinuation = ({
  basketIdent,
  items,
  claimToken = null
}) => {
  if (!basketIdent || !Array.isArray(items) || !items.length) return;
  writeJson(getStorage('local'), CLAIM_STORAGE_KEYS.CHECKOUT, {
    basketIdent,
    items,
    claimToken,
    createdAt: Date.now()
  });
};

export const getCheckoutContinuation = (basketIdent = null) => {
  const storage = getStorage('local');
  const continuation = readJson(storage, CLAIM_STORAGE_KEYS.CHECKOUT, null);
  if (
    !continuation ||
    !isFresh(continuation.createdAt, CHECKOUT_CONTINUATION_TTL_MS) ||
    (basketIdent && continuation.basketIdent !== basketIdent)
  ) {
    storage?.removeItem(CLAIM_STORAGE_KEYS.CHECKOUT);
    return null;
  }
  return continuation;
};

export const clearCheckoutContinuation = () => {
  getStorage('local')?.removeItem(CLAIM_STORAGE_KEYS.CHECKOUT);
};

export const getPackageCustomData = (packageId, claimToken) => {
  if (!claimToken || !isRetroFootballPackage(packageId)) return undefined;
  return {
    retro_claim_token: claimToken,
    schema_version: 1
  };
};

export const fetchFootballClaimStatus = async (claimToken, { signal } = {}) => {
  if (!claimToken) {
    throw new Error('Missing delivery claim token.');
  }

  const response = await fetch(`${FOOTBALL_FULFILLMENT_URL}/api/claim-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    cache: 'no-store',
    credentials: 'omit',
    signal,
    body: JSON.stringify({ claim_token: claimToken })
  });

  if (!response.ok) {
    throw new Error(
      response.status >= 500
        ? 'Delivery service is temporarily unavailable.'
        : 'Delivery status could not be read.'
    );
  }

  return response.json();
};
